import React from "react";
import { type RegisterOptions, useController } from "react-hook-form";

import PhoneVerificationInput from "@/form/inputs/PhoneVerificationInput";
import { useFieldIds } from "@/form/useFieldIds";
import { cn } from "@/lib/utils";

import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

export type PhoneVerificationFieldProps = {
  readonly label: string;
  readonly hint?: string;
  readonly name: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly hideLabel?: boolean;
  readonly hideValidation?: boolean;
  readonly onChange?: (value: string) => void;
  readonly onFinalChange?: (value: string) => void;
  readonly rules?: RegisterOptions;
};

export default function PhoneVerificationField({
  label,
  hint,
  name,
  disabled = false,
  hideLabel = false,
  hideValidation = false,
  onFinalChange,
  rules,
}: PhoneVerificationFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId, describedBy } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
    rules,
  });

  const invalid = !!fieldState.error;

  const handleChange = (value: string) => {
    field.onChange(value);
  };

  return (
    <div
      className={cn(
        "PhoneVerificationField flex w-full max-w-[330px] flex-col items-center gap-4"
      )}
      data-testid="PhoneVerificationField"
    >
      {!hideLabel && (
        <FieldLabel
          htmlFor={`${inputId}-0`}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      )}

      <PhoneVerificationInput
        id={inputId}
        name={name}
        value={field.value || ""}
        onChange={handleChange}
        onFinalChange={onFinalChange}
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
      />

      {invalid && !hideValidation && !disabled && (
        <span className="block px-4 pt-1 text-body3 text-red">
          Failed to verify
        </span>
      )}

      <div className={cn("flex h-3 justify-start", hideValidation && "hidden")}>
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
